/*
INTELLECTUAL PROPERTY NOTICE
© Orbit Tickets Ltd. All rights reserved.
This file contains Custom / Included Intellectual Property created for and owned by the Customer as part of an application developed by Kaleida within the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, the inclusion of any application within the Platform does not restrict the Customer's right to use Custom / Included Intellectual Property outside of the Platform.
*/

// IP-NOTICE: CUSTOM
import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';

export type OrbitSlideToggleLabelPosition = 'left' | 'right';
export type OrbitSlideToggleSize = 'large' | 'small';

let nextUniqueId = 0;

@Component({
	selector: 'orbit-slide-toggle',
	templateUrl: './orbit-slide-toggle.component.html',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => OrbitSlideToggleComponent),
			multi: true,
		},
	],
	host: {
		'[attr.id]': 'null',
		'[class.orbit-slide-toggle--large]': "size === 'large'",
		'[class.orbit-slide-toggle--small]': "size === 'small'",
	},
	standalone: false,
})
export class OrbitSlideToggleComponent implements ControlValueAccessor {
	@Input() id = `orbit-slide-toggle-${nextUniqueId++}`;
	@Input() size: OrbitSlideToggleSize = 'small';
	@Input() labelPosition: OrbitSlideToggleLabelPosition = 'right';
	@Input() ariaLabel?: string;
	@Input() required = false;
	@Input() tabIndex = 0;
	@Output() change = new EventEmitter<MatSlideToggleChange>();

	checked = false;
	controlDisabled = false;
	inputDisabled = false;

	@Input()
	set disabled(value: boolean | string) {
		this.inputDisabled = value !== false && value !== 'false' && value !== null && value !== undefined;
	}

	@Input('checked')
	set checkedInput(value: boolean) {
		this.checked = value === true;
	}

	get isDisabled(): boolean {
		return this.controlDisabled || this.inputDisabled;
	}

	get materialLabelPosition(): 'before' | 'after' {
		return this.labelPosition === 'left' ? 'before' : 'after';
	}

	onToggle(change: MatSlideToggleChange): void {
		this.checked = change.checked;
		this.onChange(this.checked);
		this.onTouched();
		this.change.emit(change);
	}

	onBlur(): void {
		this.onTouched();
	}

	writeValue(value: boolean | null | undefined): void {
		this.checked = value === true;
	}

	registerOnChange(fn: (value: boolean) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.controlDisabled = isDisabled;
	}

	private onChange: (value: boolean) => void = () => {};
	private onTouched: () => void = () => {};
}
